/**
 * Configuration centralisée du client
 * Ce fichier permet de gérer facilement plusieurs clients avec des branches différentes
 */

export interface ClientConfig {
  name: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    full: string;
  };
  social: {
    instagram?: string;
    facebook?: string;
    tiktok?: string;
  };
  branding: {
    logo: string;
    favicon: string;
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
  };
  location: {
    lat: number;
    lng: number;
  };
  timezone: string;
  hours: {
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
    sunday: string;
  };
  features: {
    googleCalendar: boolean;
    testimonials: boolean;
    team: boolean;
    careers: boolean;
    sms: boolean;
    email: boolean;
  };
  urls: {
    website: string;
    admin: string;
  };
}

// Configuration par défaut basée sur les variables d'environnement
const clientConfig: ClientConfig = {
  name: process.env.REACT_APP_CLIENT_NAME || 'Desiree African Hair Braiding',
  email: process.env.REACT_APP_CLIENT_EMAIL || 'desireeafricanbraiding@gmail.com',
  phone: process.env.REACT_APP_CLIENT_PHONE || '+1 909-258-8634',
  
  address: {
    street: process.env.REACT_APP_CLIENT_ADDRESS_STREET || '8560 Vineyard Ave Unit 101',
    city: process.env.REACT_APP_CLIENT_ADDRESS_CITY || 'Rancho Cucamonga',
    state: process.env.REACT_APP_CLIENT_ADDRESS_STATE || 'CA',
    zip: process.env.REACT_APP_CLIENT_ADDRESS_ZIP || '91730',
    country: process.env.REACT_APP_CLIENT_ADDRESS_COUNTRY || 'États-Unis',
    full: process.env.REACT_APP_CLIENT_ADDRESS_FULL || '8560 Vineyard Ave Unit 101, Rancho Cucamonga, CA 91730, États-Unis'
  },
  
  social: {
    instagram: process.env.REACT_APP_CLIENT_INSTAGRAM || '@desireeafricanbraiding',
    facebook: process.env.REACT_APP_CLIENT_FACEBOOK,
    tiktok: process.env.REACT_APP_CLIENT_TIKTOK
  },
  
  branding: {
    logo: process.env.REACT_APP_CLIENT_LOGO || '/logo.svg',
    favicon: process.env.REACT_APP_CLIENT_FAVICON || '/favicon.ico',
    primaryColor: process.env.REACT_APP_CLIENT_PRIMARY_COLOR || '#8B7355',
    secondaryColor: process.env.REACT_APP_CLIENT_SECONDARY_COLOR || '#6B5B95',
    accentColor: process.env.REACT_APP_CLIENT_ACCENT_COLOR || '#e91e63'
  },
  
  location: {
    lat: parseFloat(process.env.REACT_APP_CLIENT_LAT || '34.1064'),
    lng: parseFloat(process.env.REACT_APP_CLIENT_LNG || '-117.5931')
  },
  
  timezone: process.env.REACT_APP_CLIENT_TIMEZONE || 'America/Los_Angeles',
  
  hours: {
    monday: process.env.REACT_APP_HOURS_MONDAY || '9h00 - 19h00',
    tuesday: process.env.REACT_APP_HOURS_TUESDAY || '9h00 - 19h00',
    wednesday: process.env.REACT_APP_HOURS_WEDNESDAY || '9h00 - 19h00',
    thursday: process.env.REACT_APP_HOURS_THURSDAY || '9h00 - 19h00',
    friday: process.env.REACT_APP_HOURS_FRIDAY || '9h00 - 19h00',
    saturday: process.env.REACT_APP_HOURS_SATURDAY || '9h00 - 17h00',
    sunday: process.env.REACT_APP_HOURS_SUNDAY || 'Fermé'
  },
  
  features: {
    googleCalendar: process.env.REACT_APP_FEATURE_GOOGLE_CALENDAR === 'true',
    testimonials: process.env.REACT_APP_FEATURE_TESTIMONIALS !== 'false',
    team: process.env.REACT_APP_FEATURE_TEAM !== 'false',
    careers: process.env.REACT_APP_FEATURE_CAREERS !== 'false',
    sms: process.env.REACT_APP_FEATURE_SMS === 'true',
    email: process.env.REACT_APP_FEATURE_EMAIL !== 'false'
  },
  
  urls: {
    website: process.env.REACT_APP_WEBSITE_URL || 'https://haircutwebsite.web.app',
    admin: process.env.REACT_APP_ADMIN_EMAIL || 'desireeafricanbraiding@gmail.com'
  }
};

export default clientConfig;

// Helper functions
export const getFullAddress = (): string => {
  return clientConfig.address.full;
};

export const getShortAddress = (): string => {
  return `${clientConfig.address.street}, ${clientConfig.address.city}, ${clientConfig.address.state} ${clientConfig.address.zip}`;
};

export const getInstagramUrl = (): string | undefined => {
  const handle = clientConfig.social.instagram;
  if (!handle) return undefined;
  const cleanHandle = handle.replace('@', '');
  return `https://www.instagram.com/${cleanHandle}/`;
};

export const getFacebookUrl = (): string | undefined => {
  const handle = clientConfig.social.facebook;
  if (!handle) return undefined;
  return handle.startsWith('http') ? handle : `https://www.facebook.com/${handle}`;
};

export const getFormattedPhone = (): string => {
  // Format: +1 909-258-8634 -> (909) 258-8634
  const cleaned = clientConfig.phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    const match = cleaned.match(/^1(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
  }
  return clientConfig.phone;
};
